import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { faIR } from 'date-fns-jalali/locale/fa-IR';
import { format as formatJalali, formatDistanceToNowStrict } from 'date-fns-jalali';

import { toPersianDigits } from './persian-validation';

// ----------------------------------------------------------------------

dayjs.extend(duration);

export const formatStr = {
  dateTime: 'YYYY/MM/DD - HH:mm',
  date: 'YYYY/MM/DD',
  time: 'HH:mm',
  split: {
    dateTime: 'YYYY/MM/DD - HH:mm',
    date: 'YYYY/MM/DD',
  },
  paramCase: {
    dateTime: 'YYYY-MM-DD-HH-mm',
    date: 'YYYY-MM-DD',
  },
};

const jalaliFormatTokens = {
  'YYYY/MM/DD - HH:mm': 'yyyy/MM/dd - HH:mm',
  'YYYY/MM/DD': 'yyyy/MM/dd',
  'YYYY-MM-DD-HH-mm': 'yyyy-MM-dd-HH-mm',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD MMM': 'dd MMM',
  DD: 'dd',
  'HH:mm': 'HH:mm',
};

function displayDate(date, format, fallbackFormat) {
  const value = new Date(date);

  if (Number.isNaN(value.getTime())) return 'تاریخ نامعتبر';

  const selectedFormat = jalaliFormatTokens[format ?? fallbackFormat] ?? format ?? fallbackFormat;
  return toPersianDigits(formatJalali(value, selectedFormat, { locale: faIR }));
}

export function today(format) {
  return dayjs(new Date()).startOf('day').format(format);
}

export function fDateTime(date, format) {
  if (!date) return null;
  return displayDate(date, format, formatStr.dateTime);
}

export function fDate(date, format) {
  if (!date) return null;
  return displayDate(date, format, formatStr.date);
}

export function fTime(date, format) {
  if (!date) return null;
  return displayDate(date, format, formatStr.time);
}

export function fTimestamp(date) {
  if (!date) return null;

  const isValid = dayjs(date).isValid();
  return isValid ? dayjs(date).valueOf() : 'تاریخ نامعتبر';
}

export function fToNow(date) {
  if (!date) return null;

  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return 'تاریخ نامعتبر';

  return toPersianDigits(formatDistanceToNowStrict(value, { locale: faIR }));
}

export function fIsBetween(inputDate, startDate, endDate) {
  if (!inputDate || !startDate || !endDate) return false;

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate && formattedStartDate && formattedEndDate) {
    return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
  }

  return false;
}

export function fIsAfter(startDate, endDate) {
  return dayjs(startDate).isAfter(endDate);
}

export function fIsSame(startDate, endDate, units) {
  if (!startDate || !endDate) return false;

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();
  if (!isValid) return 'تاریخ نامعتبر';

  return dayjs(startDate).isSame(endDate, units ?? 'year');
}

export function fDateRangeShortLabel(startDate, endDate, initial) {
  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();
  const isAfter = fIsAfter(startDate, endDate);

  if (!isValid || isAfter) return 'تاریخ نامعتبر';

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;
  if (initial) return label;

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  return dayjs()
    .add(dayjs.duration({ years, months, days, hours, minutes, seconds, milliseconds }))
    .format();
}

export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  return dayjs()
    .subtract(dayjs.duration({ years, months, days, hours, minutes, seconds, milliseconds }))
    .format();
}
